import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Camera, Paperclip, MessageSquare, 
  CheckCircle2, XCircle, AlertTriangle, QrCode,
  ChevronLeft, ChevronRight, Send, Loader2, Eye,
  Sparkles, AlertCircle, X
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useTrace } from '../context/TraceContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { tasks } from '../data/mockData';
import { toast } from 'sonner';

// Enhanced question schema with requiresProof
const checklistQuestionsWithProof = [
  { id: 'Q1', text: 'Is the area free from previous product residues?', type: 'pass_fail', required: true, onFail: 'create_deviation', requiresProof: true },
  { id: 'Q2', text: 'Are all equipment status labels current and visible?', type: 'pass_fail', required: true, onFail: 'create_deviation', requiresProof: true },
  { id: 'Q3', text: 'Is the room temperature within specification (20-25°C)?', type: 'numeric', min: 20, max: 25, unit: '°C', required: true, onFail: 'create_deviation', requiresProof: false },
  { id: 'Q4', text: 'Is the relative humidity within specification (40-60%)?', type: 'numeric', min: 40, max: 60, unit: '%', required: true, onFail: 'create_deviation', requiresProof: false },
  { id: 'Q5', text: 'Line clearance complete and verified?', type: 'pass_fail', required: true, onFail: 'create_deviation', requiresProof: true },
  { id: 'Q6', text: 'All personnel properly gowned?', type: 'pass_fail', required: true, onFail: 'create_deviation', requiresProof: true },
  { id: 'Q7', text: 'Batch record available and verified?', type: 'pass_fail', required: true, onFail: 'create_deviation', requiresProof: true },
  { id: 'Q8', text: 'Raw materials verified against BOM?', type: 'pass_fail', required: true, onFail: 'create_deviation', requiresProof: true }
];

// Simulated AI responses
const aiResponses = {
  ok: [
    { notes: ['Surface appears clean with no visible residue', 'Lighting conditions adequate for inspection'], confidence: 94 },
    { notes: ['Equipment labels are clearly visible', 'Calibration dates are current'], confidence: 91 },
    { notes: ['Area meets cleanliness standards', 'No cross-contamination indicators detected'], confidence: 96 },
    { notes: ['Personnel PPE appears compliant', 'Gowning procedure followed correctly'], confidence: 89 },
  ],
  flag: [
    { notes: ['Possible residue detected in corner area', 'Recommend manual verification'], confidence: 72 },
    { notes: ['Label partially obscured', 'Date may be approaching expiry'], confidence: 68 },
    { notes: ['Lighting insufficient for conclusive analysis', 'Suggest additional photo'], confidence: 65 },
    { notes: ['Minor discrepancy in gowning observed', 'Review recommended'], confidence: 74 },
  ]
};

const IPadModeExecution = () => {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const { pidTraceMode } = useTrace();
  const fileInputRef = useRef(null);
  
  const task = tasks.find(t => t.id === taskId) || tasks[0];
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questionData, setQuestionData] = useState({});
  const [showDeviationModal, setShowDeviationModal] = useState(false);
  const [deviationComment, setDeviationComment] = useState('');
  const [deviationSeverity, setDeviationSeverity] = useState('Major');
  const [scannedRoom, setScannedRoom] = useState(null);
  const [scannedEquipment, setScannedEquipment] = useState(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showAINotesModal, setShowAINotesModal] = useState(false);

  const questions = checklistQuestionsWithProof;
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const currentQ = questions[currentQuestion];
  
  // Get current question data
  const currentQData = questionData[currentQ.id] || {
    photoUrls: [],
    aiStatus: 'idle',
    aiResult: null,
    aiNotes: [],
    confidence: 0,
    answer: null,
    comment: ''
  };

  // Check if PASS/FAIL should be enabled
  const isPassFailEnabled = () => {
    if (currentQ.type !== 'pass_fail') return true;
    if (!currentQ.requiresProof) return true;
    return currentQData.aiStatus === 'done';
  };

  // Check if comment is required
  const isCommentRequired = () => {
    if (currentQData.answer === 'fail') return true;
    if (currentQData.answer === 'pass' && currentQData.aiResult === 'flag') return true;
    return false;
  };

  // Update question data
  const updateQuestionData = (questionId, updates) => {
    setQuestionData(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...updates
      }
    }));
  };

  // Handle photo capture
  const handleCapturePhoto = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Create a fake URL for the photo
    const photoUrl = URL.createObjectURL(file);
    
    // Update state with photo and start AI processing
    updateQuestionData(currentQ.id, {
      photoUrls: [...(currentQData.photoUrls || []), photoUrl],
      aiStatus: 'processing',
      aiResult: null,
      aiNotes: [],
      confidence: 0
    });

    // Simulate AI processing
    setTimeout(() => {
      // Randomly decide AI result (70% ok, 30% flag)
      const isOk = Math.random() > 0.3;
      const responses = isOk ? aiResponses.ok : aiResponses.flag;
      const response = responses[Math.floor(Math.random() * responses.length)];

      updateQuestionData(currentQ.id, {
        aiStatus: 'done',
        aiResult: isOk ? 'ok' : 'flag',
        aiNotes: response.notes,
        confidence: response.confidence
      });

      toast.success('AI analysis complete');
    }, 2500);

    // Reset file input
    e.target.value = '';
  };

  const handleAnswer = (answer) => {
    updateQuestionData(currentQ.id, { answer });

    // Check if comment is required but not provided
    if (answer === 'fail' || (answer === 'pass' && currentQData.aiResult === 'flag')) {
      // Don't auto-advance, let user add comment
      return;
    }

    if (answer === 'fail' && currentQ.onFail === 'create_deviation') {
      setShowDeviationModal(true);
    } else if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    }
  };

  const handleCommentChange = (comment) => {
    updateQuestionData(currentQ.id, { comment });
  };

  const handleConfirmWithComment = () => {
    if (currentQData.answer === 'fail' && currentQ.onFail === 'create_deviation') {
      setShowDeviationModal(true);
    } else if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handleNumericAnswer = (value) => {
    const numValue = parseFloat(value);
    const isPass = numValue >= currentQ.min && numValue <= currentQ.max;
    updateQuestionData(currentQ.id, { answer: isPass ? 'pass' : 'fail' });
    
    if (!isPass && currentQ.onFail === 'create_deviation') {
      setShowDeviationModal(true);
    } else if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 500);
    }
  };

  const handleCreateDeviation = () => {
    setShowDeviationModal(false);
    toast.success('Deviation DEV-OSD-2026-014 created', {
      description: 'Redirecting to deviation detail...',
      action: {
        label: 'View',
        onClick: () => navigate('/deviations/DEV-OSD-2026-014')
      }
    });
    
    setTimeout(() => {
      navigate('/deviations/DEV-OSD-2026-014');
    }, 2000);
  };

  const handleSimulateScan = (type, value) => {
    if (type === 'room') {
      setScannedRoom(value);
    } else {
      setScannedEquipment(value);
    }
    setShowScanModal(false);
    toast.success(`${type === 'room' ? 'Room' : 'Equipment'} scanned: ${value}`);
  };

  const handleSubmit = () => {
    const allAnswered = questions.every(q => questionData[q.id]?.answer);
    if (!allAnswered) {
      toast.error('Please answer all questions before submitting');
      return;
    }
    
    const hasFails = Object.values(questionData).some(q => q.answer === 'fail');
    if (hasFails) {
      toast.warning('Checklist completed with failures', {
        description: 'Deviations have been created for failed items'
      });
    } else {
      toast.success('Checklist completed successfully!', {
        description: 'All items passed verification'
      });
    }
    
    setTimeout(() => navigate('/tasks'), 2000);
  };

  const canProceed = () => {
    if (!currentQData.answer) return false;
    if (isCommentRequired() && !currentQData.comment?.trim()) return false;
    return true;
  };

  return (
    <div className="min-h-screen bg-[#f5f6f8] text-slate-900" data-testid="ipad-mode">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelected}
      />

      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm px-4 py-3">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tasks')}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Exit
          </Button>
          <div className="text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider">iPad Mode</p>
            <p className="text-sm font-medium text-slate-800">{task.name}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* <ReportExportButton 
              title="iPad Execution Report"
              subtitle="Checklist execution with AI-powered evidence verification"
            /> */}
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
              Schedule-M
            </Badge>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="max-w-3xl mx-auto mt-3">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>Question {currentQuestion + 1} of {questions.length}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2 bg-slate-200" />
        </div>
      </div>

      {/* Scan Section */}
      <div className="max-w-3xl mx-auto px-4 py-4">
        <Card className={`bg-white border border-slate-200 shadow-sm mb-4 relative ${pidTraceMode ? 'border-cyan-400' : ''}`}>
          <TraceLabel prd="PRD §6.3" req="SM-DI-001" />
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
                onClick={() => setShowScanModal(true)}
                data-testid="scan-qr-btn"
              >
                <QrCode className="h-4 w-4 mr-2" />
                Scan QR
              </Button>
              <div className="flex-1 grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-md p-2 border border-slate-200">
                  <p className="text-xs text-slate-500">Room</p>
                  <p className="text-sm text-slate-800 font-mono">{scannedRoom || 'Not scanned'}</p>
                </div>
                <div className="bg-slate-50 rounded-md p-2 border border-slate-200">
                  <p className="text-xs text-slate-500">Equipment</p>
                  <p className="text-sm text-slate-800 font-mono">{scannedEquipment || 'Not scanned'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Question Card */}
        <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-400' : ''}`}>
          <TraceLabel prd="PRD §6.3" req="SM-SAN-001" />
          <CardContent className="p-6">
            {/* Question Text */}
            <div className="text-center mb-6">
              <Badge className="bg-slate-100 text-slate-600 border border-slate-200 mb-4">
                Question {currentQuestion + 1}
                {currentQ.requiresProof && (
                  <span className="ml-2 text-[#fd4816]">• Photo Required</span>
                )}
              </Badge>
              <h2 className="text-xl font-medium text-slate-900 mb-2">
                {currentQ.text}
              </h2>
              {currentQ.type === 'numeric' && (
                <p className="text-sm text-slate-500">
                  Acceptable range: {currentQ.min} - {currentQ.max} {currentQ.unit}
                </p>
              )}
            </div>

            {/* Proof Capture Section - Only for pass_fail questions */}
            {currentQ.type === 'pass_fail' && (
              <>
                {/* Proof Capture Buttons */}
                <div className="flex justify-center gap-3 mb-6">
                  <Button 
                    className="bg-[#fd4816] hover:bg-[#ea380c] text-white"
                    onClick={handleCapturePhoto}
                    data-testid="capture-photo-btn"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Capture Photo
                  </Button>
                  <Button variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-50">
                    <Paperclip className="h-4 w-4 mr-2" />
                    Attach
                  </Button>
                  <Button variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-50">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Comment
                  </Button>
                </div>

                {/* Photo Preview */}
                {currentQData.photoUrls?.length > 0 && (
                  <div className="flex gap-2 justify-center mb-4">
                    {currentQData.photoUrls.map((url, idx) => (
                      <div key={idx} className="relative w-16 h-16 rounded-md overflow-hidden border border-slate-200">
                        <img src={url} alt={`Proof ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}

                {/* AI Verification Status Panel */}
                <div className="mb-6">
                  {currentQData.aiStatus === 'idle' && currentQ.requiresProof && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                      <Sparkles className="h-5 w-5 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm text-slate-500">Capture a photo to enable SwishX AI verification</p>
                    </div>
                  )}

                  {currentQData.aiStatus === 'processing' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                        <span className="text-sm font-medium text-blue-700">SwishX AI is checking…</span>
                      </div>
                      <p className="text-xs text-blue-600">Analyzing image for compliance indicators</p>
                    </div>
                  )}

                  {currentQData.aiStatus === 'done' && currentQData.aiResult === 'ok' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-700">AI Result: Looks compliant</span>
                      </div>
                      <ul className="text-xs text-emerald-600 space-y-1 ml-7">
                        {currentQData.aiNotes.map((note, idx) => (
                          <li key={idx}>• {note}</li>
                        ))}
                      </ul>
                      <button 
                        className="text-xs text-emerald-700 underline mt-2 ml-7"
                        onClick={() => setShowAINotesModal(true)}
                      >
                        View AI notes
                      </button>
                    </div>
                  )}

                  {currentQData.aiStatus === 'done' && currentQData.aiResult === 'flag' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-5 w-5 text-amber-600" />
                        <span className="text-sm font-medium text-amber-700">AI Result: Potential gap detected</span>
                      </div>
                      <ul className="text-xs text-amber-600 space-y-1 ml-7">
                        {currentQData.aiNotes.map((note, idx) => (
                          <li key={idx}>• {note}</li>
                        ))}
                      </ul>
                      <button 
                        className="text-xs text-amber-700 underline mt-2 ml-7"
                        onClick={() => setShowAINotesModal(true)}
                      >
                        View AI notes
                      </button>
                    </div>
                  )}
                </div>

                {/* PASS/FAIL Buttons */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <Button
                    size="lg"
                    disabled={!isPassFailEnabled()}
                    className={`h-20 text-lg transition-all ${
                      currentQData.answer === 'pass' 
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 ring-2 ring-emerald-100' 
                        : isPassFailEnabled()
                          ? 'bg-white hover:bg-emerald-50 border border-slate-200 text-slate-700 hover:border-emerald-300'
                          : 'bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                    onClick={() => isPassFailEnabled() && handleAnswer('pass')}
                    data-testid="pass-btn"
                  >
                    <CheckCircle2 className="h-6 w-6 mr-2" />
                    PASS
                  </Button>
                  <Button
                    size="lg"
                    disabled={!isPassFailEnabled()}
                    className={`h-20 text-lg transition-all ${
                      currentQData.answer === 'fail' 
                        ? 'bg-red-600 hover:bg-red-700 text-white border-red-600 ring-2 ring-red-100' 
                        : currentQData.aiResult === 'flag' && isPassFailEnabled()
                          ? 'bg-white hover:bg-red-50 border-2 border-red-300 text-slate-700 hover:border-red-400'
                          : isPassFailEnabled()
                            ? 'bg-white hover:bg-red-50 border border-slate-200 text-slate-700 hover:border-red-300'
                            : 'bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                    onClick={() => isPassFailEnabled() && handleAnswer('fail')}
                    data-testid="fail-btn"
                  >
                    <XCircle className="h-6 w-6 mr-2" />
                    FAIL
                  </Button>
                </div>

                {/* Helper text when locked */}
                {!isPassFailEnabled() && (
                  <p className="text-center text-sm text-slate-500 mb-4">
                    Capture proof first to unlock response.
                  </p>
                )}

                {/* Comment Section - Shows when required */}
                {isCommentRequired() && (
                  <div className="border-t border-slate-200 pt-4">
                    <label className="text-sm text-slate-600 mb-2 block">
                      {currentQData.answer === 'pass' && currentQData.aiResult === 'flag' 
                        ? 'Explain why PASS despite AI flag (required)'
                        : 'Add comment (required for FAIL)'}
                    </label>
                    <Textarea
                      placeholder="Describe your observation..."
                      value={currentQData.comment || ''}
                      onChange={(e) => handleCommentChange(e.target.value)}
                      className="bg-white border-slate-200 text-slate-800 mb-3"
                      rows={2}
                      data-testid="question-comment"
                    />
                    <Button
                      className="w-full bg-[#fd4816] hover:bg-[#ea380c] text-white"
                      disabled={!currentQData.comment?.trim()}
                      onClick={handleConfirmWithComment}
                    >
                      Confirm {currentQData.answer === 'pass' ? 'PASS' : 'FAIL'} with Comment
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Numeric Input - Keep original flow */}
            {currentQ.type === 'numeric' && (
              <div className="mb-6">
                <div className="flex items-center gap-2 max-w-xs mx-auto">
                  <Input
                    type="number"
                    placeholder={`Enter value (${currentQ.unit})`}
                    className="bg-white border-slate-200 text-slate-900 text-center text-xl h-16"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleNumericAnswer(e.target.value);
                      }
                    }}
                    data-testid="numeric-input"
                  />
                  <span className="text-slate-500">{currentQ.unit}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="outline"
            disabled={currentQuestion === 0}
            onClick={() => setCurrentQuestion(currentQuestion - 1)}
            className="border-slate-200 text-slate-600 hover:bg-slate-50 disabled:bg-slate-50 disabled:text-slate-400"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          
          {currentQuestion === questions.length - 1 ? (
            <Button
              className="bg-[#fd4816] hover:bg-[#ea380c] text-white"
              onClick={handleSubmit}
              data-testid="submit-checklist"
            >
              <Send className="h-4 w-4 mr-2" />
              Submit Checklist
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentQuestion(currentQuestion + 1)}
              disabled={!canProceed()}
              className="bg-[#fd4816] hover:bg-[#ea380c] text-white disabled:bg-slate-200 disabled:text-slate-400"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Answered Summary */}
        <div className="mt-6 flex flex-wrap gap-2 justify-center">
          {questions.map((q, idx) => {
            const qData = questionData[q.id];
            return (
              <button
                key={q.id}
                onClick={() => setCurrentQuestion(idx)}
                className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                  qData?.answer === 'pass' ? 'bg-emerald-600 text-white' :
                  qData?.answer === 'fail' ? 'bg-red-600 text-white' :
                  idx === currentQuestion ? 'bg-[#fd4816] text-white' :
                  'bg-slate-200 text-slate-600 border border-slate-300'
                }`}
              >
                {idx + 1}
              </button>
            );
          })}
        </div>
      </div>

      {/* AI Notes Modal */}
      <Dialog open={showAINotesModal} onOpenChange={setShowAINotesModal}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#fd4816]" />
              SwishX AI Analysis
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className={`rounded-lg p-4 mb-4 ${
              currentQData.aiResult === 'ok' 
                ? 'bg-emerald-50 border border-emerald-200' 
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`text-sm font-medium ${
                  currentQData.aiResult === 'ok' ? 'text-emerald-700' : 'text-amber-700'
                }`}>
                  {currentQData.aiResult === 'ok' ? 'Looks Compliant' : 'Potential Gap Detected'}
                </span>
                <Badge className={`${
                  currentQData.aiResult === 'ok' 
                    ? 'bg-emerald-100 text-emerald-700 border-emerald-300' 
                    : 'bg-amber-100 text-amber-700 border-amber-300'
                }`}>
                  {currentQData.confidence}% confidence
                </Badge>
              </div>
              
              <p className="text-xs text-slate-500 mb-2">Analysis Notes:</p>
              <ul className={`text-sm space-y-2 ${
                currentQData.aiResult === 'ok' ? 'text-emerald-600' : 'text-amber-600'
              }`}>
                {currentQData.aiNotes?.map((note, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span>•</span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-slate-500">
              AI analysis is provided as guidance. Final decision rests with the inspector.
            </p>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowAINotesModal(false)}
              className="bg-[#fd4816] hover:bg-[#ea380c] text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deviation Modal */}
      <Dialog open={showDeviationModal} onOpenChange={setShowDeviationModal}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Create Deviation
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              This item failed verification. A deviation will be created to track this issue.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-600 mb-2 block">Severity</label>
              <Select value={deviationSeverity} onValueChange={setDeviationSeverity}>
                <SelectTrigger className="bg-white border-slate-200 text-slate-800" data-testid="severity-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="Critical" className="text-slate-800">Critical</SelectItem>
                  <SelectItem value="Major" className="text-slate-800">Major</SelectItem>
                  <SelectItem value="Minor" className="text-slate-800">Minor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-slate-600 mb-2 block">Comment (Required)</label>
              <Textarea
                placeholder="Describe the issue..."
                value={deviationComment}
                onChange={(e) => setDeviationComment(e.target.value)}
                className="bg-white border-slate-200 text-slate-800"
                rows={3}
                data-testid="deviation-comment"
              />
            </div>

            {/* Show AI analysis if available */}
            {currentQData.aiResult && (
              <div className={`rounded-md p-3 border ${
                currentQData.aiResult === 'flag' 
                  ? 'bg-amber-50 border-amber-200' 
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <p className="text-xs text-slate-500 mb-1">SwishX AI Notes</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  {currentQData.aiNotes?.map((note, idx) => (
                    <li key={idx}>• {note}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
              <p className="text-xs text-slate-500 mb-1">Evidence Attached</p>
              <div className="flex gap-2">
                {currentQData.photoUrls?.length > 0 ? (
                  currentQData.photoUrls.map((url, idx) => (
                    <div key={idx} className="w-12 h-12 rounded border border-slate-200 overflow-hidden">
                      <img src={url} alt={`Evidence ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))
                ) : (
                  <Button size="sm" variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-100">
                    <Camera className="h-3 w-3 mr-1" />
                    Add Photo
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeviationModal(false)}
              className="border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDeviation}
              disabled={!deviationComment}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="create-deviation-btn"
            >
              Create Deviation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan Modal */}
      <Dialog open={showScanModal} onOpenChange={setShowScanModal}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Simulate QR Scan</DialogTitle>
            <DialogDescription className="text-slate-500">
              Select a room or equipment to simulate scanning
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-slate-600 mb-2">Rooms</p>
              <div className="grid grid-cols-2 gap-2">
                {['RM-OSD-101', 'RM-OSD-102', 'RM-STER-001', 'RM-QC-101'].map(room => (
                  <Button
                    key={room}
                    variant="outline"
                    className="border-slate-200 text-slate-700 hover:bg-slate-50"
                    onClick={() => handleSimulateScan('room', room)}
                  >
                    {room}
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <p className="text-sm text-slate-600 mb-2">Equipment</p>
              <div className="grid grid-cols-2 gap-2">
                {['EQ-MIX-022', 'EQ-BAL-017', 'EQ-GRAN-001', 'EQ-COAT-001'].map(eq => (
                  <Button
                    key={eq}
                    variant="outline"
                    className="border-slate-200 text-slate-700 hover:bg-slate-50"
                    onClick={() => handleSimulateScan('equipment', eq)}
                  >
                    {eq}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IPadModeExecution;
